import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import FloatingBookButton from "../components/FloatingBookButton";
import ResultsCTA from "../components/ResultsCTA";
import { api, userAuth } from "../lib/api";
import { toast } from "sonner";
import {
  Target,
  Lightning,
  CurrencyDollar,
  ChartLineUp,
  FloppyDisk,
  TrashSimple,
  ArrowRight,
} from "@phosphor-icons/react";

const GOAL_TYPES = [
  { value: "retirement_income", label: "Retirement Income for Life", suggested: "annuity" },
  { value: "lump_sum", label: "Hit a Specific Dollar Target", suggested: "iul" },
  { value: "family_legacy", label: "Tax-Free Family Legacy", suggested: "whole_life" },
  { value: "business_capital", label: "Build Business / Real-Estate Capital", suggested: "iul" },
  { value: "education", label: "Fund Kids' / Grandkids' Education", suggested: "iul" },
];

const PRODUCTS = [
  { value: "iul", label: "IUL" },
  { value: "annuity", label: "FIA" },
  { value: "whole_life", label: "Whole Life" },
];

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();

export default function GoalLab() {
  // Default product comes from the quiz recommendation if it exists in localStorage
  const initialProduct = (() => {
    try {
      const raw = localStorage.getItem("ex_user_recommendation");
      if (raw) {
        const rec = JSON.parse(raw);
        if (rec?.primary && ["iul", "annuity", "whole_life"].includes(rec.primary)) {
          return rec.primary;
        }
      }
    } catch (e) { /* no-op */ }
    return "annuity";
  })();

  const [form, setForm] = useState({
    goal_type: "retirement_income",
    product: initialProduct,
    current_age: 35,
    target_age: 65,
    monthly_contribution: 500,
    target_amount: 0,
    lump_sum: 0,
    label: "",
    notes: "",
  });
  const [solveMode, setSolveMode] = useState("project");
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const loggedIn = userAuth.isLoggedIn();

  // Load saved scenarios on mount (only network effect — no state-init from storage)
  useEffect(() => {
    if (loggedIn) {
      api.get("/goals/scenarios").then((r) => setSaved(r.data.scenarios || [])).catch(() => {});
    }
  }, [loggedIn]);

  // Live recompute on any input change
  useEffect(() => {
    const body = {
      ...form,
      monthly_contribution: solveMode === "solve_monthly" ? 0 : Number(form.monthly_contribution) || 0,
      target_amount: solveMode === "solve_monthly" ? Number(form.target_amount) || 0 : Number(form.target_amount) || 0,
      lump_sum: Number(form.lump_sum) || 0,
    };
    const t = setTimeout(() => {
      api.post("/goals/project", body).then((r) => setResult(r.data)).catch(() => {});
    }, 220);
    return () => clearTimeout(t);
  }, [form, solveMode]);

  const setF = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const saveScenario = async () => {
    if (!loggedIn) {
      toast.error("Create an account to save scenarios");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/goals/scenarios", {
        ...form,
        monthly_contribution: Number(form.monthly_contribution) || 0,
        target_amount: Number(form.target_amount) || 0,
        lump_sum: Number(form.lump_sum) || 0,
      });
      setSaved((prev) => [r.data.scenario, ...prev]);
      toast.success("Scenario saved to your locker");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await api.delete(`/goals/scenarios/${id}`);
      setSaved((prev) => prev.filter((s) => s.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-exodus-bg text-exodus-text" data-testid="goal-lab-page">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-10">
        <p className="eyebrow"><span className="gold-divider" />The Goal Lab</p>
        <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">
          Set a goal. <span className="gold-text italic">Stress-test</span> the strategy.
        </h1>
        <p className="text-lg text-exodus-muted mt-6 max-w-2xl leading-relaxed">
          Pick what you actually want — lifetime income, a million-dollar legacy, a college fund —
          and play with the levers until the numbers line up. Save your scenarios. Bring them to
          our session.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-32 grid lg:grid-cols-12 gap-8">
        {/* INPUTS */}
        <div className="lg:col-span-5 card-surface p-6 lg:p-8 space-y-6">
          {/* Goal type */}
          <div>
            <label className="eyebrow text-xs block mb-3">1. What&apos;s the goal?</label>
            <div className="grid grid-cols-1 gap-2">
              {GOAL_TYPES.map((g) => (
                <button
                  key={g.value}
                  data-testid={`goal-${g.value}`}
                  onClick={() => {
                    setForm((s) => ({ ...s, goal_type: g.value, product: g.suggested }));
                  }}
                  className={`text-left px-4 py-3 border transition-colors ${
                    form.goal_type === g.value
                      ? "border-exodus-gold bg-exodus-elevated"
                      : "border-exodus-border hover:border-exodus-gold/40"
                  }`}
                >
                  <Target size={14} className="inline mr-2 text-exodus-gold" weight="thin" />
                  <span className="text-sm">{g.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Solver toggle */}
          <div>
            <label className="eyebrow text-xs block mb-3">2. What do you want me to solve?</label>
            <div className="flex gap-2">
              <button
                data-testid="solve-project"
                onClick={() => setSolveMode("project")}
                className={`flex-1 text-xs px-3 py-2 border ${
                  solveMode === "project"
                    ? "border-exodus-gold text-exodus-gold"
                    : "border-exodus-border text-exodus-muted"
                }`}
              >
                Project what I&apos;ll have
              </button>
              <button
                data-testid="solve-monthly"
                onClick={() => setSolveMode("solve_monthly")}
                className={`flex-1 text-xs px-3 py-2 border ${
                  solveMode === "solve_monthly"
                    ? "border-exodus-gold text-exodus-gold"
                    : "border-exodus-border text-exodus-muted"
                }`}
              >
                Solve the monthly I need
              </button>
            </div>
          </div>

          {/* Numbers */}
          <div className="grid grid-cols-2 gap-4">
            <NumField label="Your age" testid="goal-age" value={form.current_age} setValue={setF("current_age")} min={18} max={85} />
            <NumField label="Target age" testid="goal-target-age" value={form.target_age} setValue={setF("target_age")} min={form.current_age + 1} max={95} />
            {solveMode === "project" ? (
              <NumField label="Monthly contribution ($)" testid="goal-monthly" value={form.monthly_contribution} setValue={setF("monthly_contribution")} min={0} max={50000} step={25} />
            ) : (
              <NumField label="Target amount ($)" testid="goal-target" value={form.target_amount} setValue={setF("target_amount")} min={0} max={20000000} step={1000} />
            )}
            <NumField label="Lump sum today ($)" testid="goal-lump" value={form.lump_sum} setValue={setF("lump_sum")} min={0} max={5000000} step={1000} />
          </div>

          {/* Product */}
          <div>
            <label className="eyebrow text-xs block mb-3">3. Which strategy?</label>
            <div className="flex gap-2">
              {PRODUCTS.map((p) => (
                <button
                  key={p.value}
                  data-testid={`goal-product-${p.value}`}
                  onClick={() => setF("product")(p.value)}
                  className={`flex-1 text-sm py-2 border ${
                    form.product === p.value
                      ? "border-exodus-gold text-exodus-gold"
                      : "border-exodus-border text-exodus-muted"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Save */}
          {loggedIn && (
            <div className="border-t border-exodus-border pt-5">
              <input
                data-testid="goal-label"
                value={form.label}
                onChange={(e) => setF("label")(e.target.value)}
                placeholder="Scenario nickname (e.g. 'My early retirement plan')"
                className="w-full bg-transparent border border-exodus-border focus:border-exodus-gold px-3 py-2 text-sm mb-3 focus:outline-none"
              />
              <button
                data-testid="goal-save"
                onClick={saveScenario}
                disabled={busy}
                className="btn-primary w-full disabled:opacity-50"
              >
                <FloppyDisk size={14} weight="bold" /> Save to my locker
              </button>
            </div>
          )}
          {!loggedIn && (
            <div className="border-t border-exodus-border pt-5">
              <Link to="/signup" className="text-xs gold-text hover:underline">
                Create a free account to save scenarios →
              </Link>
            </div>
          )}
        </div>

        {/* RESULT */}
        <div className="lg:col-span-7 space-y-5">
          {result && (
            <>
              <div className="card-surface p-6 lg:p-8" data-testid="goal-result-summary">
                <p className="eyebrow text-xs">Projected outcome</p>
                <h2 className="font-display text-3xl mt-2">
                  At age {result.target_age}, you&apos;ll have{" "}
                  <span className="gold-text">{fmt(result.projected_value)}</span>
                </h2>
                {result.solved_monthly != null && (
                  <p className="text-sm text-exodus-muted mt-3">
                    To hit <strong>{fmt(form.target_amount)}</strong> in {result.years} years,
                    you&apos;d need to contribute{" "}
                    <strong className="text-exodus-gold">${result.solved_monthly.toLocaleString()}</strong>/mo.
                  </p>
                )}
                {result.shortfall > 0 && (
                  <p className="text-sm text-amber-400 mt-3">
                    ⚠ Falls short of your target by <strong>{fmt(result.shortfall)}</strong> — bump the
                    monthly or start earlier.
                  </p>
                )}
                {result.on_track && (
                  <p className="text-sm text-emerald-400 mt-3">
                    ✓ On track to hit your <strong>{fmt(form.target_amount)}</strong> target.
                  </p>
                )}
              </div>

              {/* Income card */}
              <div className="card-surface p-6 lg:p-8">
                <p className="eyebrow text-xs">Income phase — what this becomes</p>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <Stat label="Annual income"
                        value={fmt(result.income_phase.annual_tax_free_income || result.income_phase.annual_lifetime_income || 0)}
                        icon={CurrencyDollar} />
                  <Stat label="Monthly check"
                        value={fmt(result.income_phase.monthly_tax_free_income || result.income_phase.monthly_lifetime_income || 0)}
                        icon={ChartLineUp} />
                  <Stat label="Death benefit (est)"
                        value={fmt(result.death_benefit_estimate)}
                        icon={Lightning} />
                  <Stat label="Years working for you"
                        value={result.years}
                        icon={Target} />
                </div>
                <p className="text-xs text-exodus-muted mt-5 italic">
                  Model: {result.income_phase.model}. Rate assumption: {result.income_phase.rate_assumption}.
                </p>
              </div>

              {/* Why this works */}
              {result.rationale?.length > 0 && (
                <div className="card-surface p-6">
                  <p className="eyebrow text-xs mb-3">Why this strategy fits this goal</p>
                  <ul className="space-y-2">
                    {result.rationale.map((r, i) => (
                      <li key={i} className="text-sm leading-relaxed border-l-2 border-exodus-gold/60 pl-3">
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Results-focused Book CTA */}
              <ResultsCTA
                testid="goal-results-cta"
                heroLabel={
                  form.product === "annuity"
                    ? "Guaranteed lifetime monthly check"
                    : "Projected value at your target age"
                }
                heroValue={fmt(
                  form.product === "annuity"
                    ? (result.income_phase?.monthly_lifetime_income || 0)
                    : result.projected_value
                )}
                heroSuffix={form.product === "annuity" ? "/mo" : ""}
                subline={
                  form.product === "annuity"
                    ? `Contractual, for life. Built on your inputs across ${result.years} years.`
                    : `Across ${result.years} years at a ${result.rate_used ? (result.rate_used*100).toFixed(2) : "mid-case"}% net rate.`
                }
              />
            </>
          )}

          {/* Saved scenarios */}
          {loggedIn && saved.length > 0 && (
            <div className="card-surface p-6">
              <p className="eyebrow text-xs mb-3">Your saved scenarios ({saved.length})</p>
              <ul className="space-y-3">
                {saved.map((s) => (
                  <li key={s.id} data-testid={`saved-${s.id}`} className="flex items-center gap-3 border-l-2 border-exodus-border pl-3 py-1">
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-sm truncate">{s.label}</p>
                      <p className="text-[11px] text-exodus-muted truncate">
                        {s.product.toUpperCase()} · {s.goal_type.replace("_", " ")} · {fmt(s.result?.projected_value)} by {s.target_age}
                      </p>
                    </div>
                    <button
                      data-testid={`del-${s.id}`}
                      onClick={() => deleteScenario(s.id)}
                      className="text-exodus-muted hover:text-red-400 shrink-0"
                    >
                      <TrashSimple size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingBookButton testid="goal-floating-book" />
    </div>
  );
}

function NumField({ label, value, setValue, min, max, step = 1, testid }) {
  return (
    <label className="block">
      <span className="eyebrow text-[10px] block mb-1">{label}</span>
      <input
        type="number"
        data-testid={testid}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => setValue(e.target.value === "" ? 0 : Number(e.target.value))}
        className="w-full bg-transparent border border-exodus-border focus:border-exodus-gold focus:outline-none px-3 py-2 text-sm"
      />
    </label>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-exodus-border p-3">
      <Icon size={14} className="text-exodus-gold" weight="thin" />
      <p className="text-[10px] eyebrow text-exodus-muted mt-1">{label}</p>
      <p className="font-display text-xl mt-1">{value}</p>
    </div>
  );
}
